import React, { useState } from 'react';
import { Button } from './Button';
import { useToast } from '../../hooks/useToast';
import { useMoveToProductionMutation } from '../../store/slices/apiSlice';
import type { MoveToProductionButtonProps } from '../../types';
import { Factory } from 'lucide-react';

export const MoveToProductionButton: React.FC<MoveToProductionButtonProps> = ({
  jobId,
  disabled = false,
  onSuccess
}) => {
  const toast = useToast();
  const [moveToProduction, { isLoading }] = useMoveToProductionMutation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [notes, setNotes] = useState('');

  const handleConfirm = async () => {
    try {
      await moveToProduction({
        id: jobId,
        notes: notes.trim() || undefined
      }).unwrap();
      
      toast.success('Success', 'Job moved to production workflow');
      setShowConfirm(false);
      setNotes('');
      onSuccess?.();
    } catch (error: any) {
      toast.error('Failed to move to production', error?.data?.detail || 'Please try again');
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowConfirm(true)}
        disabled={disabled || isLoading}
        icon={<Factory className="w-4 h-4" />}
        className="bg-orange-600 hover:bg-orange-700 text-white"
      >
        Move to Production (New Orders)
      </Button>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowConfirm(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Factory className="h-6 w-6 text-orange-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Move Job to Production
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This job will be moved to the production workflow and appear in the "New Orders" tab on the Production page.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <label htmlFor="production-notes" className="block text-sm font-medium text-gray-700">
                  Notes (Optional)
                </label>
                <textarea
                  id="production-notes"
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 sm:text-sm p-3"
                  placeholder="Add any notes about why this job is ready for production..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  autoFocus
                />
              </div>
              
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center sm:ml-3 sm:w-auto bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {isLoading ? 'Moving...' : 'Move to Production'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center sm:mt-0 sm:w-auto"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
